import { useNavigation } from '@react-navigation/native';
import * as ExpoLocation from 'expo-location';
import { useFormikContext } from 'formik';
import { TFunction } from 'i18next';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import {
  GooglePlacesAutocomplete,
  GooglePlacesAutocompleteRef,
} from 'react-native-google-places-autocomplete';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Highlighter from '@components/HighlightWords';
import { WebHoverBox } from '@components/Modals/WebHoverBox';
import { FormValues } from '@components/Projects/ProjectFormStack';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { WebIcon } from '@components/shared/Icon/WebIcon';
import theme from '@themes/theme';
export type Location = {
  address: string;
  latitude: number;
  longitude: number;
};

interface LocationSearchProps {
  onSelect: (location: Location) => void;
  onClose?: () => void;
  isShowCancel?: boolean;
  /** TA-10950 #19: the standardized 48px/white/Grey02-black-focus search-box
   * spec. Scoped to Create Project only (Jo's sign-off, recorded on
   * TA-10950) - every other screen sharing this component (Edit Profile,
   * Create Team, chat Map) keeps the pre-existing layout via the 'legacy'
   * default, so only ProjectLocationSearch.tsx passes 'standard'. */
  variant?: 'legacy' | 'standard';
}

const LocationSearchLeftIcon = () => (
  <Icon name='Search' variant='m' color='black' />
);

const createLocationSearchEmptyState = (t: TFunction) => () => (
  <Box flex={1} paddingTop='m' alignItems='center' marginBottom='xs'>
    <Text>{t('models:location.notFound')}</Text>
  </Box>
);

export const LocationSearch: React.FC<LocationSearchProps> = ({
  onSelect,
  onClose,
  isShowCancel = true,
  variant = 'legacy',
}) => {
  const navigation = useNavigation();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const ref = useRef<GooglePlacesAutocompleteRef>(null);
  const [enableHighAccuracy, setEnableHighAccuracy] = useState(false);
  // TA-10950 #19: standard search-box spec (white bg, Grey02 default border,
  // black focus border) - tracked locally since the border color is baked
  // into GooglePlacesAutocomplete's static `styles.textInput` prop.
  const [isFocused, setIsFocused] = useState(false);
  // TA-10950 #19: GooglePlacesAutocomplete owns the typed query in its own
  // internal state and doesn't expose it to `renderRightButton` - mirror it
  // here so the clear-X can show as soon as there's text to clear, not only
  // once a place has actually been selected (values.location).
  const [searchText, setSearchText] = useState('');

  const requestLocationPermissions = async () => {
    const response = await ExpoLocation.requestForegroundPermissionsAsync();

    if (response.granted) {
      setEnableHighAccuracy(true);
    }
  };

  useEffect(() => {
    requestLocationPermissions();
  }, []);

  const { values } = useFormikContext<FormValues>();

  useEffect(() => {
    if (values.location?.address && ref.current) {
      ref.current.setAddressText(values.location.address);
    }
  }, [values.location?.address]);

  const isStandard = variant === 'standard';

  const getRenderRow = (data: any, index: number) => {
    let name = data.description;
    let address = '';
    if (data.description.indexOf(',') > 0) {
      name = data.description.substring(0, data.description.indexOf(','));
      address = data.description.substring(data.description.indexOf(',') + 2);
    }
    const content = (
      <>
        <Text variant='buttonLabel' color='textPrimary' numberOfLines={1}>
          <Highlighter
            autoEscape
            highlightStyle={{
              color: theme.colors.grey04,
              fontFamily: 'Inter_500Medium',
              backgroundColor: theme.colors.background,
            }}
            searchWords={[ref?.current?.getAddressText() || '']}
            textToHighlight={name}
          />
        </Text>
        {address !== '' && (
          <Text
            variant='labelSmall'
            color='grey04'
            marginTop='xxs'
            numberOfLines={1}>
            {address}
          </Text>
        )}
      </>
    );
    // TA-10950 #19: the Grey01 list-item hover is part of the standardized
    // search box - scoped to Create Project with the rest of the variant
    // (see `variant` doc above), so other screens keep the plain row.
    return isStandard ? (
      <WebHoverBox
        accessibilityLabel='Address'
        key={index}
        alignItems='flex-start'
        width='100%'>
        {content}
      </WebHoverBox>
    ) : (
      <Box accessibilityLabel='Address' key={index} alignItems='flex-start'>
        {content}
      </Box>
    );
  };

  if (!isStandard) {
    // Pre-TA-10950 layout: 36px grey02 input, X replaces Cancel once a place
    // is selected. Kept as the default for every screen except Create
    // Project - see the `variant` prop doc above.
    return (
      <Box flex={1} style={{ marginTop: insets.top }} backgroundColor='white'>
        <Box flexDirection='row' alignItems='center'>
          <Box
            style={{
              ...StyleSheet.absoluteFillObject,
              zIndex: 2,
              paddingTop: theme.spacing.m,
              paddingLeft: theme.spacing.xs,
            }}
            pointerEvents='none'>
            <Icon name='Search' variant='m' color='grey04' />
          </Box>

          <GooglePlacesAutocomplete
            ref={ref}
            enableHighAccuracyLocation={enableHighAccuracy}
            placeholder='Search'
            query={{
              key: process.env.GOOGLE_PLACES_API_KEY,
              language: 'en',
            }}
            requestUrl={{
              useOnPlatform: 'web',
              url: 'https://cjf69wd314.execute-api.us-east-1.amazonaws.com/dev',
            }}
            fetchDetails={true}
            onPress={(data, details = null) => {
              const location = {
                address: details?.formatted_address || data.description,
                latitude: details?.geometry.location.lat || 0,
                longitude: details?.geometry.location.lng || 0,
              };
              onSelect(location);
              onClose ? onClose() : navigation.pop();
            }}
            textInputProps={{
              InputComp: TextInput,
              autoFocus: true,
              errorStyle: { color: theme.colors.alertRed },
              clearButtonMode: true,
              leftIcon: LocationSearchLeftIcon,
            }}
            listEmptyComponent={createLocationSearchEmptyState(t)}
            renderRow={(data, index) => getRenderRow(data, index)}
            renderRightButton={() => {
              if (values.location) {
                return (
                  <TouchableOpacity
                    onPress={() => {
                      onSelect({
                        address: '',
                        latitude: 0,
                        longitude: 0,
                      });

                      onClose ? onClose() : navigation.pop();
                    }}
                    style={{ paddingHorizontal: theme.spacing.s }}>
                    <Icon name='Close' variant='s' color='grey04' />
                  </TouchableOpacity>
                );
              } else
                return (
                  isShowCancel && (
                    <TouchableOpacity
                      onPress={() => (onClose ? onClose() : navigation.pop())}>
                      <Text marginLeft='xs' variant='buttonLabel' color='blue'>
                        {t('shared:cancel')}
                      </Text>
                    </TouchableOpacity>
                  )
                );
            }}
            styles={{
              textInput: {
                height: 36,
                backgroundColor: theme.colors.grey02,
                paddingVertical: 0,
                paddingLeft: theme.spacing.xl,
                marginTop: theme.spacing.xs,
                fontSize: 16,
                fontWeight: '500',
                borderRadius: theme.borderRadii.xs,
              },
              textInputContainer: {
                alignItems: 'center',
              },
              row: {
                paddingVertical: theme.spacing.xxs,
              },
              separator: {
                height: 1,
                backgroundColor: theme.colors.grey02,
              },
              poweredContainer: {
                justifyContent: 'center',
                borderColor: theme.colors.grey02,
                borderTopWidth: 1,
              },
            }}
            predefinedPlaces={[]}
            minLength={2}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box flex={1} style={{ marginTop: insets.top }} backgroundColor='white'>
      {/* alignItems='flex-start': the results dropdown renders in normal
          flow below the input and grows this row's height - 'center' would
          re-center Cancel against that full (growing) height instead of
          keeping it pinned next to the input band. */}
      <Box flexDirection='row' alignItems='flex-start'>
        {/* TA-10950 #19: wraps just the bordered input (+ its two icon
            overlays) so the clear-X can anchor to this box's own right
            edge, not the full row's - Cancel sits outside, as a real flex
            sibling, since it renders at its own intrinsic width. */}
        <Box flex={1}>
          <Box
            style={{
              position: 'absolute',
              top: theme.spacing.xs,
              left: 0,
              // Matches the textInput's own marginTop (top) and height
              // (below) exactly, so justifyContent='center' centers the
              // icon within the same 48px band the input's text sits in -
              // a fixed paddingTop from the old 36px input left the icon
              // sitting visibly higher than the text once the input grew
              // to 48px.
              height: theme.spacing.xxl,
              zIndex: 2,
              paddingLeft: theme.spacing.xs,
            }}
            justifyContent='center'
            pointerEvents='none'>
            <Icon name='Search' variant='m' color='grey04' />
          </Box>

          {/* Clear-X lives inside the bordered input (right edge),
              matching other standardized search boxes - mirrors the left
              search-icon overlay above. */}
          {(values.location || searchText) && (
            <Box
              style={{
                position: 'absolute',
                top: theme.spacing.xs,
                right: 0,
                height: theme.spacing.xxl,
                zIndex: 2,
                paddingRight: theme.spacing.xs,
              }}
              justifyContent='center'>
              <WebIcon
                name='Close'
                variant='s'
                color='grey04'
                accessibilityLabel={
                  values.location ? 'Clear address' : 'Clear search'
                }
                onPress={() => {
                  if (values.location) {
                    onSelect({ address: '', latitude: 0, longitude: 0 });
                    onClose ? onClose() : navigation.pop();
                  } else {
                    ref.current?.setAddressText('');
                    setSearchText('');
                    ref.current?.focus();
                  }
                }}
              />
            </Box>
          )}

          <GooglePlacesAutocomplete
            ref={ref}
            enableHighAccuracyLocation={enableHighAccuracy}
            placeholder='Search'
            query={{
              key: process.env.GOOGLE_PLACES_API_KEY,
              language: 'en',
            }}
            requestUrl={{
              useOnPlatform: 'web',
              url: 'https://cjf69wd314.execute-api.us-east-1.amazonaws.com/dev',
            }}
            fetchDetails={true}
            onPress={(data, details = null) => {
              const location = {
                address: details?.formatted_address || data.description,
                latitude: details?.geometry.location.lat || 0,
                longitude: details?.geometry.location.lng || 0,
              };
              onSelect(location);
              onClose ? onClose() : navigation.pop();
            }}
            textInputProps={{
              InputComp: TextInput,
              autoFocus: true,
              errorStyle: { color: theme.colors.alertRed },
              clearButtonMode: true,
              leftIcon: LocationSearchLeftIcon,
              onFocus: () => setIsFocused(true),
              onBlur: () => setIsFocused(false),
              onChangeText: setSearchText,
              placeholderTextColor: theme.colors.grey04,
            }}
            listEmptyComponent={createLocationSearchEmptyState(t)}
            renderRow={(data, index) => getRenderRow(data, index)}
            styles={{
              // TA-10950 #19: standard search-box spec - white bg, 1px
              // Grey02 default border, black focus border, 48px height,
              // 8px radius.
              textInput: {
                height: theme.spacing.xxl,
                backgroundColor: theme.colors.white,
                borderWidth: 1,
                borderColor: isFocused
                  ? theme.colors.black
                  : theme.colors.grey02,
                paddingVertical: 0,
                paddingLeft: theme.spacing.xl,
                // Reserves room for the clear-X overlay on the right so
                // typed text never runs underneath it.
                paddingRight: theme.spacing.xl,
                marginTop: theme.spacing.xs,
                fontSize: 16,
                fontWeight: '500',
                borderRadius: theme.borderRadii.xs,
                // Suppresses the browser's native blue focus ring so our
                // own black focus border (above) is the only focus
                // affordance - same pattern as FilterSheetSearchInput's
                // webFocusReset.
                outlineStyle: 'none',
                // `as any`: GooglePlacesAutocomplete's textInput style type
                // is plain RN ViewStyle/TextStyle and doesn't declare the
                // web-only `outlineStyle` CSS property above, so the
                // object literal fails its declared type without this
                // escape hatch.
              } as any,
              textInputContainer: {
                alignItems: 'center',
              },
              row: {
                paddingVertical: theme.spacing.xxs,
              },
              separator: {
                height: 1,
                backgroundColor: theme.colors.grey02,
              },
              poweredContainer: {
                justifyContent: 'center',
                borderColor: theme.colors.grey02,
                borderTopWidth: 1,
              },
            }}
            predefinedPlaces={[]}
            minLength={2}
          />
        </Box>

        {isShowCancel && (
          // Matches the search icon/clear-X overlays' own band exactly
          // (marginTop xs + height xxl, centered within) so Cancel stays
          // vertically aligned with the input regardless of dropdown height.
          <Box
            height={theme.spacing.xxl}
            marginTop='xs'
            justifyContent='center'>
            <TouchableOpacity
              onPress={() => (onClose ? onClose() : navigation.pop())}>
              <Text marginLeft='xs' variant='buttonLabel' color='blue'>
                {t('shared:cancel')}
              </Text>
            </TouchableOpacity>
          </Box>
        )}
      </Box>
    </Box>
  );
};
